import React from "react";
import { useSelector } from "react-redux";
import styles from "../widget-renderer.module.scss";
import localStyles from "./multiple-text-values-widget.module.scss";
import type { WidgetRendererProps } from "../widget-renderer";
import { selectUnitsById } from "@src/reducers/units-slice";
import { getUnitById } from "@src/cloud-connect/units/utils/get-unit-by-id";
import type {
  EncodingTransform,
  MultipleTextValuesRow,
} from "@src/types/dashboards";
import { useMultipleTextValues } from "./use-multiple-text-values";
import { DURATION_GAUGE_WIDGET_MS, isoToDurationMs } from "../constants";
import { applyFieldTransforms } from "../utils/value-scaling";
import { formatWidgetDisplayValue } from "../utils/format-display-value";
import { NoDataInTimeframe } from "@src/components/atoms/no-data-in-timeframe/no-data-in-timeframe";
import Spinner from "@src/components/atoms/spinner/spinner";

const cssClassName = "multiple-text-values-widget";

/** Picker-tile content. The template ships a single entry, so without this
 *  the tile would show one em-dash and read as broken. Mocking stays inside
 *  `preview` — a real dashboard never shows a fabricated number. */
const MOCK_ROWS: MultipleTextValuesRow[] = [
  {
    id: "mock-0",
    field: "value_0",
    label: "Temperature",
    order: 0,
    unit: null,
  },
  { id: "mock-1", field: "value_1", label: "Pressure", order: 1, unit: null },
  { id: "mock-2", field: "value_2", label: "State", order: 2, unit: null },
];
const MOCK_VALUES = [56.7897778, 12.4, "running"];
const MOCK_UNITS = ["°C", "bar", ""];

/** Every entry is one grid row and the tile's height is locked to the entry
 *  count, so all entries are the same height and the type scale is fixed.
 *  The label and the unit are ratios of the value. */
const VALUE_FONT_SIZE = 20;

/** One row of the tile: the label on the left, the value and unit on the
 *  right. The parent hands over the fetched value, the unit text, and the
 *  font and colour settings. Splitting the row out keeps the widget body
 *  short. */
interface MultipleTextValuesRowViewProps {
  row: MultipleTextValuesRow;
  raw: number | string | null;
  transforms: EncodingTransform[] | null | undefined;
  unitText: string;
  labelSize: number;
  valueSize: number;
  unitSize: number;
  labelColor: string | undefined;
  valueColor: string | undefined;
}

const MultipleTextValuesRowView = ({
  row,
  raw,
  transforms,
  unitText,
  labelSize,
  valueSize,
  unitSize,
  labelColor,
  valueColor,
}: MultipleTextValuesRowViewProps) => {
  // A row can carry its own scale factor. `applyFieldTransforms` multiplies
  // numbers by that factor and leaves strings and nulls alone, so a factor
  // on a text row, or on a row with no data, changes nothing. Numbers round
  // to at most two decimals for display, so 1.234 reads as 1.23.
  const scaled = applyFieldTransforms(row.field, raw, transforms);
  const valueText = formatWidgetDisplayValue(scaled);
  return (
    <div className={localStyles[`${cssClassName}__row`]}>
      {row.label && (
        <div
          className={localStyles[`${cssClassName}__label`]}
          style={{ fontSize: labelSize, color: labelColor }}
        >
          {row.label}
        </div>
      )}
      <div className={localStyles[`${cssClassName}__value-group`]}>
        <div
          className={localStyles[`${cssClassName}__value`]}
          style={{ fontSize: valueSize, color: valueColor }}
        >
          {valueText}
        </div>
        {unitText && (
          <div
            className={localStyles[`${cssClassName}__unit`]}
            style={{ fontSize: unitSize, color: valueColor }}
          >
            {unitText}
          </div>
        )}
      </div>
    </div>
  );
};

/** A table of up to 10 values, each bound to its own machine + node. Rows
 *  are laid out label-left / value+unit-right, with the gap between them
 *  growing as the tile widens. The value is the part that truncates.
 *
 *  The tile's height is not the user's to pick: it comes from the entry
 *  count, and the drawer writes it into the positioning on save. */
export const MultipleTextValuesWidget = ({
  encoding,
  presentation,
  query,
  preview = false,
  inViewport = true,
}: WidgetRendererProps) => {
  const units = useSelector(selectUnitsById);
  const rows: MultipleTextValuesRow[] = preview
    ? MOCK_ROWS
    : encoding?.rows ?? [];

  const live = useMultipleTextValues(
    preview ? undefined : query,
    isoToDurationMs(query?.window?.duration) ?? DURATION_GAUGE_WIDGET_MS,
    inViewport,
  );
  // The first paint happens before the hook's effect has flipped its own
  // loading flag, so a bound widget would flash a table of em-dashes.
  // Treat "bound but nothing back yet" as loading too. Keyed on the binding
  // COUNT rather than on `query` existing: a query with no bindings has
  // nothing to wait for, so such a widget renders its rows (a freshly placed
  // tile shows em-dashes) instead of spinning forever.
  const hasBindings = (query?.bindings?.length ?? 0) > 0;
  const isLoading =
    !preview && (live.isLoading || (hasBindings && live.entries.length === 0));
  const isEmpty = !preview && live.hasNoData;

  const valueSize = VALUE_FONT_SIZE;
  const labelSize = Math.round(valueSize * 0.85);
  const unitSize = Math.round(valueSize * 0.6);

  const labelColor = presentation?.label_color ?? undefined;
  const valueColor = presentation?.color ?? undefined;

  return (
    <div className={styles["widget-renderer__body"]}>
      {isLoading ? (
        <div className={styles["widget-renderer__loading"]}>
          <Spinner />
        </div>
      ) : isEmpty ? (
        <NoDataInTimeframe />
      ) : (
        <div className={localStyles[cssClassName]}>
          {rows.map((row, index) => {
            const raw = preview
              ? MOCK_VALUES[index]
              : live.entries[index]?.value ?? null;
            const unit = preview ? null : getUnitById(units, row.unit);
            const unitText = preview
              ? MOCK_UNITS[index] ?? ""
              : unit?.unit_text?.eng?.symbol ||
                unit?.unit_text?.eng?.name ||
                "";
            return (
              <MultipleTextValuesRowView
                key={row.id}
                row={row}
                raw={raw}
                transforms={encoding?.transforms}
                unitText={unitText}
                labelSize={labelSize}
                valueSize={valueSize}
                unitSize={unitSize}
                labelColor={labelColor}
                valueColor={valueColor}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
